import React, { useState, useEffect } from "react";
import axios from "axios";
import styles from '../styles/pages/loans_deposits.module.css';
import LoanspageModal from "../components/LoanspageModal";
import { FaEdit, FaPlus } from "react-icons/fa";
import { useAuth } from "../authentication/AuthContext";

function Loans() {
    const [loansData, setLoansData] = useState([]); // Store the loan data from the server
    const [loading, setLoading] = useState(true); // Loading state for fetching data
    const [activeLoanType, setActiveLoanType] = useState(null); // Track the active loan type
    const [activeSubcategory, setActiveSubcategory] = useState(null); // Track active subcategory
    const [isModalOpen, setIsModalOpen] = useState(false); // Modal visibility state
    const [currentLoanData, setCurrentLoanData] = useState(null); // Data for the current loan type
    const [isCreatingNewLoan, setIsCreatingNewLoan] = useState(false); // Track if we're creating a new loan
    const { isAuthenticated } = useAuth();
    // Fetch loan data from the API on page load
    useEffect(() => {
        window.scrollTo(0, 0); // Scroll to the top of the page when it's loaded
        fetchLoans();
    }, []);

    const fetchLoans = () => {
        axios.get('/api/loanspage')
            .then(response => {
                setLoansData(response.data); // Save fetched data into state
                setLoading(false);

                const firstLoan = response.data[0];
                if (firstLoan) {
                    setActiveLoanType(firstLoan.loan_type); // Set the first loan type as active
                    const hasSub = firstLoan.tabs.some(t => t.subcategoryName); // Check if subcategories exist
                    if (hasSub) {
                        setActiveSubcategory(firstLoan.tabs.find(t => t.subcategoryName)?.subcategoryName || null);
                    }
                }
            })
            .catch(error => {
                console.error("Error fetching loans data:", error);
                setLoading(false);
            });
    };

    // Helper function to safely replace string
    const safeString = (str) => (str ? str.replace("/", "_") : "");

    // Handle updating loan data (post to backend)
    const postLoanPageData = async (updatedRates) => {
        if (!updatedRates?.tabs || updatedRates.tabs.length === 0) {
            console.error("Tabs are missing or empty.");
            alert("Please check the data.");
            return;
        }

        // Prepare the loan data for submission
        const loanData = {
            loan_type: updatedRates.loan_type,
            tabs: updatedRates.tabs.map(item => ({
                subcategoryName: item.subcategoryName || null,
                brackets: item.brackets.map(bracket => ({
                    purpose: bracket.purpose || "",
                    interest_rate: Array.isArray(bracket.interest_rate) ? bracket.interest_rate : [bracket.interest_rate],
                    tenure: bracket.tenure || "",
                    requirement: Array.isArray(bracket.requirement) ? bracket.requirement : [bracket.requirement],
                    margin: bracket.margin || ""
                }))
            }))
        };

        // If it's a new loan, POST the data
        if (isCreatingNewLoan) {
            try {
                await axios.post("/api/loanspage", loanData);
                alert("New loan data created successfully!");
            } catch (error) {
                console.error("Error creating loan data:", error);
                alert("Error creating new loan.");
            }
        } else {
            // If updating an existing loan, PUT the data
            if (!updatedRates._id) {
                alert("Loan ID is missing. Cannot update.");
                return;
            }
            try {
                await axios.put(`/api/loanspage/${updatedRates._id}`, loanData);
                alert("Loan data updated successfully!");
            } catch (error) {
                console.error("Error updating loan data:", error);
                alert("Error updating loan.");
            }
        }

        fetchLoans(); // Refresh the data after successful update or creation
        setIsModalOpen(false); // Close the modal after submission
    };

    // Handle clicking on a loan type
    const handleLoanTypeClick = (loanType, tabs) => {
        setActiveLoanType(loanType); // Set the clicked loan type as active
        const hasSubcategories = tabs.some(t => t.subcategoryName); // Check if subcategories exist for the loan type
        if (hasSubcategories) {
            setActiveSubcategory(tabs.find(t => t.subcategoryName)?.subcategoryName || null); // Set the first subcategory as active if present
        } else {
            setActiveSubcategory(null); // No subcategory, set to null
        }
    };

    // Handle clicking on a subcategory
    const handleSubcategoryClick = (subcategory, parentLoanType) => {
        setActiveLoanType(parentLoanType); // Set the parent loan type as active
        setActiveSubcategory(subcategory); // Set the clicked subcategory as active
    };

    // Open the modal for editing loan data
    const handleEditLoanData = (loanData) => {
        setIsModalOpen(true); // Open the modal
        setCurrentLoanData(loanData); // Set the current loan data for the modal
        setIsCreatingNewLoan(false); // Set the state to "edit" mode
    };

    // Open the modal for creating new loan data
    const handleCreateNewLoan = () => {
        setIsModalOpen(true); // Open the modal
        setCurrentLoanData(null); // Set the current loan data to null for creating new loan
        setIsCreatingNewLoan(true); // Set the state to "create" mode
    };

    // Loading state before data is fetched
    if (loading) return <div>Loading...</div>;

    return (
        <div className={`${'body'} ${styles.body}`}>

            {/* Displaying all the loan types */}
          
            <div className={styles.tabs}>
            {isAuthenticated  && (  
            <button
                onClick={handleCreateNewLoan}
                className={styles.createNewLoanButton}
                
            >Create New</button>
        )} 
                {loansData.map(item => {
                    const hasSubcategories = item.tabs.some(t => t.subcategoryName); // Check if subcategories exist
                    return (
                        <div key={item._id}>
                            {/* Loan Type Button */}
                            <a
                                id={safeString(item.loan_type)}
                                className={item.loan_type === activeLoanType ? "activeTab" : ""}
                                onClick={() => handleLoanTypeClick(item.loan_type, item.tabs)}
                            >
                                {item.loan_type}
                            </a>
                            {/* Subcategory tabs if present */}
                            {hasSubcategories && (
                                <div className={styles.subTabs}>
                                    {item.tabs.map(tab => (
                                        tab.subcategoryName && (
                                            <a
                                                key={tab.subcategoryName}
                                                id={safeString(tab.subcategoryName)}
                                                className={tab.subcategoryName === activeSubcategory ? "activeTab" : ""}
                                                onClick={() => handleSubcategoryClick(tab.subcategoryName, item.loan_type)}
                                            >
                                                {tab.subcategoryName}
                                            </a>
                                        )
                                    ))}
                                </div>
                            )}
                            {/* Edit Button for Loan Type */}
                            {isAuthenticated  && (  
                            <button onClick={() => handleEditLoanData({ ...item, _id: item._id })}>
                                Edit
                            </button>
                            )} 
                        </div>
                    );
                })}
            </div>

            {/* Button to create new loan */}
            
            {/* Modal for editing or creating loan data */}
            <LoanspageModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onSubmit={postLoanPageData}
                currentLoanData={currentLoanData}
                isCreatingNewLoan={isCreatingNewLoan} // Pass mode to modal
            />

            {/* Render the loan data content based on the selected loan type */}
            {loansData.map(item => {
                if (item.loan_type !== activeLoanType) return null; // Skip if not the active loan type
                const hasSubcategories = item.tabs.some(t => t.subcategoryName);

                if (hasSubcategories) {
                    // Render data for selected subcategory
                    return item.tabs.map(tab => {
                        if (tab.subcategoryName === activeSubcategory) {
                            return (
                                <div key={tab.subcategoryName} className={`${safeString(tab.subcategoryName)} ${styles.loans} active`}>
                                    <div className={`${'heading'} ${styles.heading}`}>{tab.subcategoryName}</div>
                                    <hr /><hr />
                                    <div>
                                        {tab.brackets.map(bracket => (
                                            <div key={bracket._id} className={styles.table}>
                                                <div className={styles.row}><p className={styles.head}>PURPOSE</p><p>{bracket.purpose}</p></div>
                                                <div className={styles.row}><p className={styles.head}>INTEREST RATE</p><p>{bracket.interest_rate.join(", ")}</p></div>
                                                <div className={styles.row}><p className={styles.head}>TENURE</p><p>{bracket.tenure}</p></div>
                                                <div className={styles.row}><p className={styles.head}>REQUIREMENT</p><p>{bracket.requirement.join(", ")}</p></div>
                                                <div className={styles.row}><p className={styles.head}>MARGIN(Min)</p><p>{bracket.margin}</p></div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            );
                        }
                        return null;
                    });
                } else {
                    // Render data for loan type with no subcategories
                    return (
                        <div key={item.loan_type} className={`${safeString(item.loan_type)} ${styles.loans} active`}>
                            <div className={`${'heading'} ${styles.heading}`}>{item.loan_type}</div>
                            <hr /><hr />
                            <div>
                                {item.tabs[0]?.brackets.map(bracket => (
                                    <div key={bracket._id} className={styles.table}>
                                        <div className={styles.row}><p className={styles.head}>PURPOSE</p><p>{bracket.purpose}</p></div>
                                        <div className={styles.row}><p className={styles.head}>INTEREST RATE</p><p>{bracket.interest_rate.join(", ")}</p></div>
                                        <div className={styles.row}><p className={styles.head}>TENURE</p><p>{bracket.tenure}</p></div>
                                        <div className={styles.row}><p className={styles.head}>REQUIREMENT</p><p>{bracket.requirement.join(", ")}</p></div>
                                        <div className={styles.row}><p className={styles.head}>MARGIN(Min)</p><p>{bracket.margin}</p></div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    );
                }
            })}
        </div>
    );
}

export default Loans;
